import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as XLSX from "xlsx";

type ExcelViewerProps = {
  open: boolean;
  onClose: () => void;
  blob: Blob | null;
  filename?: string;
};

type SheetView = {
  name: string;
  rows: any[][];
};

export const ExcelViewer: React.FC<ExcelViewerProps> = ({
  open,
  onClose,
  blob,
  filename,
}) => {
  const theme = useTheme();
  const [sheets, setSheets] = React.useState<SheetView[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [activeIdx, setActiveIdx] = React.useState(0);

  React.useEffect(() => {
    if (!open || !blob) {
      setSheets(null);
      setError(null);
      setActiveIdx(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const parsed: SheetView[] = workbook.SheetNames.map((name) => {
          const sheet = workbook.Sheets[name];
          const rows = XLSX.utils.sheet_to_json<any[]>(sheet, {
            header: 1,
            blankrows: false,
            defval: "",
          });
          return { name, rows };
        });
        if (!cancelled) {
          setSheets(parsed);
          setActiveIdx(0);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Error al leer el archivo");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, blob]);

  const active = sheets ? sheets[activeIdx] : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      // Sobre la banda de modales/mini-forms (10100-10300) para quedar por
      // encima del modal que lo abrió; debajo de tooltip/snackbar (10400).
      sx={{ zIndex: 10350 }}
    >
      <DialogTitle>{filename ?? "Excel"}</DialogTitle>
      <DialogContent dividers sx={{ minHeight: 400 }}>
        {!sheets && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {sheets && sheets.length > 1 && (
          <Tabs
            value={activeIdx}
            onChange={(_, v) => setActiveIdx(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sheets.map((s, i) => (
              <Tab key={i} label={s.name} />
            ))}
          </Tabs>
        )}
        {active && (
          <Box sx={{ overflow: "auto", mt: 1 }}>
            <table
              style={{
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: "100%",
              }}
            >
              <tbody>
                {active.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          border: "1px solid " + theme.palette.divider,
                          padding: "4px 8px",
                          whiteSpace: "nowrap",
                          verticalAlign: "top",
                        }}
                      >
                        {cell === null || cell === undefined ? "" : String(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
